import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Cake, LogOut, Users, User as UserIcon, Search, ChevronDown, Mail, Plus } from "lucide-react";
import GlobalShareButton from "@/components/GlobalShareButton";
import CreateGroupModal from "@/components/CreateGroupModal";
import GiftFinder from "@/components/GiftFinder";
import Avatar from "@/components/Avatar";
import AlertBar from "@/components/AlertBar";
import LiveBanner from "@/components/LiveBanner";
import LiveRoomModal from "@/components/LiveRoomModal";
import SearchPalette from "@/components/SearchPalette";
import MyGroupsMenu from "@/components/MyGroupsMenu";
import { useBranding } from "@/contexts/BrandingContext";

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid="user-menu">
      <button
        onClick={() => setOpen((o) => !o)}
        className="bd-chip flex items-center gap-1.5"
        title={user.email}
        data-testid="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={user.name || user.email} picture={user.picture} size={22} testId="user-menu-avatar" />
        <span className="hidden sm:inline">{user.name?.split(" ")[0] || user.email}</span>
        <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-60 bd-surface"
          style={{
            borderRadius: "var(--radius-card)",
            boxShadow: "0 20px 50px -12px rgba(0,0,0,0.30)",
            padding: "0.5rem",
            zIndex: 60,
          }}
          role="menu"
          data-testid="user-menu-panel"
        >
          <div
            className="flex items-center gap-3 px-3 py-2"
            style={{ borderBottom: "1px solid var(--border)" }}
          >
            <Avatar name={user.name || user.email} picture={user.picture} size={36} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: "var(--text)" }}>
                {user.name || "—"}
              </div>
              <div className="text-xs truncate" style={{ color: "var(--text-dim)" }}>
                {user.email}
              </div>
            </div>
          </div>
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded text-sm hover:opacity-80"
            style={{ textDecoration: "none", color: "var(--text)" }}
            data-testid="user-menu-profile"
            role="menuitem"
          >
            <UserIcon size={14} /> Profile
          </Link>
          <Link
            to="/invites"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded text-sm hover:opacity-80"
            style={{ textDecoration: "none", color: "var(--text)" }}
            data-testid="user-menu-invites"
            role="menuitem"
          >
            <Mail size={14} /> Invites
          </Link>
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:opacity-80"
            style={{
              background: "transparent",
              border: "none",
              textAlign: "left",
              cursor: "pointer",
              color: "#ff3b30",
            }}
            data-testid="logout-btn"
            role="menuitem"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { branding, logoSrc } = useBranding();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTab, setSearchTab] = useState("people");
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const openSearch = (tab) => {
    setSearchTab(tab);
    setSearchOpen(true);
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "var(--bg)",
        color: "var(--text)",
        fontFamily: "var(--font-brand, inherit)",
      }}
    >
      <header
        className="sticky top-0 z-[60] backdrop-blur-md"
        style={{ background: "color-mix(in srgb, var(--bg) 85%, transparent)", borderBottom: "1px solid var(--border)" }}
      >
        {user && user.user_id && <AlertBar />}
        {user && user.user_id && <LiveBanner />}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2 font-extrabold text-xl" data-testid="logo-link">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={branding?.wordmark || "Born Day"}
                className="w-9 h-9 rounded"
                style={{ objectFit: "cover", border: "1px solid var(--border)" }}
                data-testid="brand-logo-img"
              />
            ) : (
              <span
                className="w-9 h-9 grid place-items-center"
                style={{
                  background: "var(--primary)",
                  color: "var(--primary-fg)",
                  borderRadius: "var(--radius-btn)",
                  border: "1px solid var(--border)",
                }}
                data-testid="brand-logo-default"
              >
                <Cake size={18} />
              </span>
            )}
            {(branding?.show_wordmark ?? true) && (
              <span style={{ letterSpacing: "-0.04em" }} data-testid="brand-wordmark">
                {branding?.wordmark || "Born Day"}
              </span>
            )}
          </Link>

          <nav className="flex items-center gap-1 sm:gap-3">
            <Link to="/app" className="bd-tab hidden sm:inline-flex" data-testid="nav-calendar">Calendar</Link>
            {user && user.user_id && (
              <>
                {/* Single universal-search spyglass. Searches across People,
                    Groups and Events from one icon — replaces the older
                    three-tab nav for a cleaner portrait layout. */}
                <button
                  type="button"
                  className="bd-tab flex items-center gap-1"
                  onClick={() => openSearch("people")}
                  title="Search Born Day"
                  aria-label="Search Born Day"
                  data-testid="nav-search"
                >
                  <Search size={14} /> <span className="hidden sm:inline">Search</span>
                </button>
                {/* Lists every group the member belongs to (not just owned). */}
                <MyGroupsMenu currentUserId={user.user_id} />
                {/* Create-new-group quick action — lives in the top-middle-right
                    of the nav per Steve's directive. Any signed-in member can
                    spin up a new group from anywhere in the app. */}
                <button
                  type="button"
                  onClick={() => setCreateGroupOpen(true)}
                  className="bd-tab flex items-center gap-1"
                  title="Create a new group"
                  aria-label="Create a new group"
                  data-testid="nav-create-group"
                >
                  <Plus size={14} /> <span className="hidden sm:inline">New group</span>
                </button>
              </>
            )}
            {user && user.user_id && (
              <div className="flex items-center gap-1 sm:gap-2 ml-1 sm:ml-3">
                <GiftFinder />
                <div className="hidden sm:flex items-center gap-2">
                  <GlobalShareButton />
                </div>
                <UserMenu user={user} onLogout={doLogout} />
              </div>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">{children}</main>

      <footer className="px-4 py-6 text-center text-xs" style={{ color: "var(--text-dim)" }}>
        {branding?.wordmark || "Born Day"} · Built for 2028 · <span className="bd-mono">v0.1</span>
      </footer>

      <SearchPalette
        open={searchOpen}
        initialTab={searchTab}
        onClose={() => setSearchOpen(false)}
      />
      <CreateGroupModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        onCreated={(g) => {
          // Land them on /groups so they see the new group with member-management UI.
          navigate("/groups");
        }}
      />
      <LiveRoomModal />
    </div>
  );
}
